import { useEffect, useId, useRef } from "react";
import { isTopDialog, popDialog, pushDialog } from "./dialog-stack";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  /** Max width — defaults to 28rem (md). */
  maxWidthClassName?: string;
  /** test id for the dialog root. */
  testId?: string;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * Lightweight modal dialog that matches the prototype's surface styling
 * (`#1f2937` surface, `#374151` border). Closes on ESC, traps focus while open,
 * and restores focus to the previously-focused element on close.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  maxWidthClassName = "max-w-md",
  testId,
}: DialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const myId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    // Register on the global dialog stack so Escape in a stacked sub-dialog
    // doesn't bubble up and close the parent.
    pushDialog(myId);

    // Remember what was focused before we opened.
    previousFocusRef.current =
      (document.activeElement as HTMLElement | null) ?? null;

    // Move focus into the dialog.
    const root = dialogRef.current;
    if (root) {
      const focusables = getFocusable(root);
      const target = focusables[0] ?? root;
      // Defer one frame so the dialog is fully rendered before focusing.
      requestAnimationFrame(() => target.focus());
    }

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Only the top-most open dialog should handle Escape.
        if (!isTopDialog(myId)) return;
        onClose();
        return;
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = getFocusable(dialogRef.current);
        if (focusables.length === 0) {
          e.preventDefault();
          dialogRef.current.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !dialogRef.current.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    window.addEventListener("keydown", handler);

    return () => {
      window.removeEventListener("keydown", handler);
      popDialog(myId);
      // Restore focus to whatever was focused before opening.
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === "function") {
        prev.focus();
      }
    };
  }, [open, onClose, myId]);

  if (!open) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      tabIndex={-1}
      data-testid={testId}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`bg-surface border border-border-subtle rounded-lg w-full ${maxWidthClassName} max-h-[85vh] flex flex-col animate-fade-in`}
      >
        <div className="px-6 pt-5 pb-3 border-b border-border-subtle shrink-0">
          <h2 id={titleId} className="text-base font-semibold text-white">
            {title}
          </h2>
          {description && (
            <p id={descriptionId} className="text-xs text-text-muted mt-1">
              {description}
            </p>
          )}
        </div>
        <div className="px-6 py-5 overflow-y-auto min-h-0">{children}</div>
      </div>
    </div>
  );
}
